import { Button, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    ImageViewer as ZhImageViewer
} from "zhihao-ui-react";

import request from "@/api";
import { customerService } from "@/api/services/customerService";
import { fileService } from "@/api/services/fileService";

export interface ZoneRechargeDetail {
    recharge?: {
        id?: string | number;
        createdTime?: string;
        beforeBalanceAmount?: number;
        rechargeAmount?: number;
        afterBalanceAmount?: number;
        customerId?: number;
        certificateId?: number;
        remark?: string;
        snapCustomerInfo?: {
            realName?: string;
            code?: string;
            customerCode?: string;
            mobile?: string;
            institutionName?: string;
        };
    };
    specialZoneName?: string;
    creator?: string;
}

export interface ZoneRechargeDetailPageProps {
    loadDetail?: (id: string) => Promise<ZoneRechargeDetail>;
}

/** 复用 Vue 专区充值明细详情接口。 */
export async function loadZoneRechargeDetail(id: string): Promise<ZoneRechargeDetail> {
    const response = await request.service.get<ZoneRechargeDetail>(`/admin/special-zone-recharge/vo/${id}`);
    return response.data;
}

/** 保留 Vue 详情页人民币金额格式。 */
function formatMoney(value?: number): string {
    return typeof value === "number" && Number.isFinite(value) ? `￥${value.toFixed(2)}` : "--";
}

/** 保留 Vue 充值主体快照的实名/编码展示。 */
function getCustomerLabel(detail: ZoneRechargeDetail, customerCodeFallback = ""): string {
    const customer = detail.recharge?.snapCustomerInfo;
    const code = customer?.code?.trim() || customer?.customerCode?.trim() || customerCodeFallback.trim();
    return [customer?.realName, code].filter(Boolean).join(" | ") || "--";
}

/** 专区充值详情页，保留 Vue 详情字段、凭证状态和返回入口。 */
export function ZoneRechargeDetailPage({ loadDetail = loadZoneRechargeDetail }: ZoneRechargeDetailPageProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id = "" } = useParams<{ id: string }>();
    const routeId = id || location.pathname.split("/").filter(Boolean).pop() || "";
    const [detail, setDetail] = useState<ZoneRechargeDetail | null>(null);
    const [customerCodeFallback, setCustomerCodeFallback] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [previewImageList, setPreviewImageList] = useState<string[]>([]);
    const [showVoucherViewer, setShowVoucherViewer] = useState(false);

    useEffect(() => {
        if (!routeId) {
            setErrorMessage("缺少充值编号");
            return;
        }
        let disposed = false;
        void loadDetail(routeId)
            .then(result => {
                if (disposed) return;
                setDetail(result);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("充值详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, routeId]);

    /** 对齐 Vue：快照没有客户编码时，按 customerId 查询当前客户编码补全展示。 */
    useEffect(() => {
        const customer = detail?.recharge?.snapCustomerInfo;
        const customerId = detail?.recharge?.customerId;
        setCustomerCodeFallback("");
        if (customer?.code?.trim() || customer?.customerCode?.trim() || customerId == null) return;
        if (!Number.isFinite(Number(customerId))) return;

        let disposed = false;
        void customerService
            .find(Number(customerId))
            .then(result => {
                if (!disposed) setCustomerCodeFallback(result.customer?.code?.trim() ?? "");
            })
            .catch(() => {
                // 快照字段优先，客户编码补全失败时保留详情页可用状态。
            });
        return () => {
            disposed = true;
        };
    }, [detail]);

    /** 按 Vue 详情页通过凭证 ID 查询文件地址，只有成功加载后才允许打开预览。 */
    useEffect(() => {
        const certificateId = detail?.recharge?.certificateId;
        setShowVoucherViewer(false);
        setPreviewImageList([]);
        if (certificateId == null) return;
        let disposed = false;
        void fileService
            .getFileById(Number(certificateId))
            .then(file => {
                if (disposed) return;
                setPreviewImageList(file.path ? [file.path] : []);
            })
            .catch(() => {
                if (!disposed) setPreviewImageList([]);
            });
        return () => {
            disposed = true;
        };
    }, [detail?.recharge?.certificateId]);

    const recharge = detail?.recharge;
    return (
        <section
            aria-label="zone-recharge-detail-page"
            className="detail-json-page detail-page-transparent admin-detail-page"
        >
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "充值编号", value: recharge?.id || routeId || "--" }]}
                    rightInfos={[{ label: "充值时间", value: recharge?.createdTime?.slice(0, 16) || "--" }]}
                    title="充值详情"
                />
            </div>
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {detail ? (
                <div className="detail-json">
                    <section className="detail-json__block">
                        <ZhGrid className="admin-detail-grid" cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="充值编号" value={<span>{recharge?.id || routeId || "--"}</span>} />
                            <ZhBaseItem
                                label="充值时间"
                                value={<span>{recharge?.createdTime?.slice(0, 16) || "--"}</span>}
                            />
                            <ZhBaseItem
                                label="充值主体"
                                value={<span>{getCustomerLabel(detail, customerCodeFallback)}</span>}
                            />
                            <ZhBaseItem label="充值专区" value={<span>{detail.specialZoneName || "--"}</span>} />
                            <ZhBaseItem
                                label="充值前余额"
                                value={<span>{formatMoney(recharge?.beforeBalanceAmount)}</span>}
                            />
                            <ZhBaseItem label="充值金额" value={<span>{formatMoney(recharge?.rechargeAmount)}</span>} />
                            <ZhBaseItem
                                label="充值后余额"
                                value={<span>{formatMoney(recharge?.afterBalanceAmount)}</span>}
                            />
                            <ZhBaseItem label="操作人" value={<span>{detail.creator || "--"}</span>} />
                            <ZhBaseItem label="备注" value={<span>{recharge?.remark?.trim() || "--"}</span>} />
                            <ZhBaseItem
                                label="凭证"
                                value={
                                    recharge?.certificateId && previewImageList.length ? (
                                        <Button type="link" onClick={() => setShowVoucherViewer(true)}>
                                            查看凭证
                                        </Button>
                                    ) : recharge?.certificateId ? (
                                        <Button type="link" disabled>
                                            查看凭证
                                        </Button>
                                    ) : (
                                        "--"
                                    )
                                }
                            />
                        </ZhGrid>
                    </section>
                </div>
            ) : null}
            <Space className="operation-fixed-btn" size={0}>
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
            {showVoucherViewer && previewImageList.length ? (
                <ZhImageViewer alt="充值凭证" urlList={previewImageList} onClose={() => setShowVoucherViewer(false)} />
            ) : null}
        </section>
    );
}

export default ZoneRechargeDetailPage;
